// Service pour l'intégration Cal.com API
// Documentation: https://cal.com/docs/api-reference/v2

import {
  GetSlotsResponse,
  CreateBookingResponse,
  CancelBookingResponse,
  RescheduleBookingResponse,
} from '@/types/scheduling';
import type { CalSlot } from '@/types/scheduling';
import { logger } from '@/utils/logger';
import { getUserById } from '../../data/users';
import { getProductById } from '../../data/products';
import { getBookingByUid, updateBookingByUid, updateBooking, createBooking as createBookingInDb, getBookingById } from '../../data/bookings';
import {
  getMonthSlotsViaEdge,
  createBookingViaEdge,
  cancelBookingViaEdge,
  rescheduleBookingViaEdge,
  callCalendlyCancelWebhookByBookingIdViaEdge,
} from './calEdgeFunction';
import { getCalSlugForBooking } from './calSlugSelector';
import { parseCalApiResponse, transformSlotsToCalSlots } from './calSlotParser';
import { initializeCalMode } from './calConfig';
import {
  createBookingPayload,
  createTempBooking,
  confirmBookingAfterCalResponse
} from './calBookingHelpers';
import { syncStaffFromCalBooking } from '../../business/staffSync';
import { getDayDateRange, getMonthDateRange } from './calDateHelpers';
import { validateCalConfig, validateFirstTimerEventTypeId } from './calValidation';
import { getBookingDuration } from './calBookingDuration';
import { extractDatesFromSlots, getUserFirstTimerStatus, getUserEditorialFirstTimerStatus } from './calMonthAvailability';
import { handleSlotsError, handleBookingError, handleCancellationError } from './calErrorHandlers';

// Initialize Cal.com mode
initializeCalMode();

/**
 * 1️⃣ Récupération des créneaux disponibles
 * 
 * @param date - Date pour laquelle récupérer les créneaux
 * @returns Liste des créneaux disponibles ou erreur
 */
/**
 * Récupère les dates qui ont au moins un créneau disponible pour un mois donné
 * @param year - Année
 * @param month - Mois (0-11, comme Date.getMonth())
 * @returns Set de dates au format YYYY-MM-DD qui ont des disponibilités
 */
export async function getMonthAvailability(
  year: number,
  month: number,
  userId?: string,
  productId?: string,
  locationId?: string,
  eventType: 'shooting' | 'preparation' | 'editorial' = 'shooting'
): Promise<Set<string>> {
  // Treat 'editorial' as 'preparation' for slot availability
  const effectiveEventType: 'shooting' | 'preparation' = eventType === 'editorial' ? 'preparation' : eventType;
  const isFirstTimer = effectiveEventType === 'shooting' 
    ? await getUserFirstTimerStatus(userId)
    : await getUserEditorialFirstTimerStatus(userId);
  
  if (!userId || !productId) {
    logger.error('❌ getMonthAvailability appelé sans userId ou productId');
    return new Set<string>();
  }

  const calConfig = await getCalSlugForBooking(productId, effectiveEventType, userId, locationId);
  if (!calConfig) {
    return new Set<string>();
  }

  const { start: startDate, end: endDate } = getMonthDateRange(year, month);
  const { eventTypeSlug, teamSlug, username } = calConfig;
  
  // For first-timers, use teamSlug instead of username
  const teamSlugToUse = isFirstTimer === true ? teamSlug : (username ? undefined : teamSlug);
  const usernameToUse = isFirstTimer === true ? undefined : username;

  try {
    logger.debug(`🔍 Chargement des disponibilités pour ${year}-${String(month + 1).padStart(2, '0')} (mois complet)`);
    logger.debug(`📅 Période: ${startDate.toISOString()} → ${endDate.toISOString()}`);

    // Fetch entire month in one API call using Cal.com v2 API
    const data = await getMonthSlotsViaEdge(
      startDate.toISOString(),
      endDate.toISOString(),
      eventTypeSlug,
      teamSlugToUse,
      { username: usernameToUse }
    );

    // Extract dates from the v2 response format (date-keyed object)
    const slotsData = parseCalApiResponse(data);
    const datesWithAvailability = extractDatesFromSlots(slotsData);

    return datesWithAvailability;
  } catch (error) {
    logger.error(`❌ Erreur lors du chargement des disponibilités pour ${year}-${String(month + 1).padStart(2, '0')}:`, error);
    throw error;
  }
}

export async function getAvailableSlots(
  date: Date,
  userId: string,
  productId: string,
  locationId?: string,
  eventType: 'shooting' | 'preparation' | 'editorial' = 'shooting'
): Promise<GetSlotsResponse> {
  // Treat 'editorial' as 'preparation' for slot availability
  const effectiveEventType: 'shooting' | 'preparation' = eventType === 'editorial' ? 'preparation' : eventType;
  
  const product = await getProductById(productId);
  const durationField = effectiveEventType === 'preparation' ? 'preparation_duration_minutes' : 'shooting_duration_minutes';
  const durationMinutes = product?.[durationField] || (effectiveEventType === 'preparation' ? 45 : 180);
  
  const calConfig = await getCalSlugForBooking(productId, effectiveEventType, userId, locationId);
  
  const validationError = validateCalConfig(calConfig, `${effectiveEventType} slots`);
  if (validationError) return validationError;
  
  const { eventTypeSlug, teamSlug, username } = calConfig!;
  
  // Check if user is first-timer based on event type
  const isFirstTimer = effectiveEventType === 'shooting'
    ? await getUserFirstTimerStatus(userId)
    : await getUserEditorialFirstTimerStatus(userId);
  
  // For first-timers, use teamSlug instead of username
  const teamSlugToUse = isFirstTimer === true ? teamSlug : (username ? undefined : teamSlug);
  const usernameToUse = isFirstTimer === true ? undefined : username;

  try {
    const { start: startDate, end: endDate } = getDayDateRange(date);

    const data = await getMonthSlotsViaEdge(
      startDate.toISOString(),
      endDate.toISOString(),
      eventTypeSlug,
      teamSlugToUse,
      { username: usernameToUse }
    );

    const slotsData = parseCalApiResponse(data);
    const slots = transformSlotsToCalSlots(slotsData, durationMinutes);

    return {
      success: true,
      slots,
    };
  } catch (error) {
    return handleSlotsError(error, date, durationMinutes);
  }
}

/**
 * Récupère tous les créneaux sur une période donnée
 */
export async function getSlotsInRange(
  start: Date,
  end: Date,
  userId: string,
  productId: string,
  locationId?: string,
  eventType: 'shooting' | 'preparation' | 'editorial' = 'shooting'
): Promise<CalSlot[]> {
  const effectiveEventType: 'shooting' | 'preparation' = eventType === 'editorial' ? 'preparation' : eventType;
  const product = await getProductById(productId);
  const durationField = effectiveEventType === 'preparation' ? 'preparation_duration_minutes' : 'shooting_duration_minutes';
  const durationMinutes = product?.[durationField] || (effectiveEventType === 'preparation' ? 45 : 180);

  const calConfig = await getCalSlugForBooking(productId, effectiveEventType, userId, locationId);
  const validationError = validateCalConfig(calConfig, `${effectiveEventType} slots`);
  if (validationError) {
    return [];
  }

  const { eventTypeSlug, teamSlug, username } = calConfig!;
  
  // Check if user is first-timer based on event type
  const isFirstTimer = effectiveEventType === 'shooting'
    ? await getUserFirstTimerStatus(userId)
    : await getUserEditorialFirstTimerStatus(userId);
  
  // For first-timers, use teamSlug instead of username
  const teamSlugToUse = isFirstTimer === true ? teamSlug : (username ? undefined : teamSlug);
  const usernameToUse = isFirstTimer === true ? undefined : username;

  const startDate = new Date(start);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);

  const data = await getMonthSlotsViaEdge(
    startDate.toISOString(),
    endDate.toISOString(),
    eventTypeSlug,
    teamSlugToUse,
    { username: usernameToUse }
  );

  const slotsData = parseCalApiResponse(data);
  return transformSlotsToCalSlots(slotsData, durationMinutes);
}

/**
 * 2️⃣ Création d'une réservation
 * 
 * ⚠️ ATTENTION : Nécessite une clé API Bearer secrète
 * Cette fonction ne peut PAS être appelée directement depuis le front
 * Vous devez créer un backend/proxy qui gère l'authentification
 * 
 * @param slotStart - Date/heure de début du créneau (ISO 8601)
 * @param userName - Nom de l'utilisateur
 * @param userEmail - Email de l'utilisateur
 * @param productId - ID du produit sélectionné (détermine le slug Cal.com via cal_event_mappings)
 * @returns Confirmation de réservation ou erreur
 */
export async function createBooking(
  slotStart: string,
  userName: string,
  userEmail: string,
  productId: string,
  userId: string,
  locationId?: string,
  existingBooking?: { booking_calendly_uri?: string | null; shooting_id?: string | null; booking_uid?: string | null; id?: string } | null
): Promise<CreateBookingResponse> {
  try {
    const calConfig = await getCalSlugForBooking(productId, 'shooting', userId, locationId);

    if (!calConfig) {
      return {
        success: false,
        error: 'Configuration Cal.com introuvable pour ce produit',
      };
    }

    const { eventTypeSlug, teamSlug, username, eventTypeId } = calConfig;
    
    const userResult = await getUserById(userId);
    const isFirstTimer = userResult.success && userResult.data ? !userResult.data.director_id : true;

    const validationError = validateFirstTimerEventTypeId(isFirstTimer, eventTypeId, eventTypeSlug);
    if (validationError) return validationError;

    const directorId = !isFirstTimer && userResult.data?.director_id ? userResult.data.director_id : undefined;

    // Create temporary booking in database
    const tempResult = await createTempBooking({
      userId,
      slotStart,
      productId,
      eventTypeSlug,
      locationId,
      existingShootingId: existingBooking?.shooting_id,
      metadata: buildLegacyCalendlyMetadata(existingBooking),
      directorId,
    });
    if (tempResult.success === false) return tempResult.error;
    const { bookingId, tempBookingUid } = tempResult;

    // Build payload with legacy metadata if needed
    const metadata = buildLegacyCalendlyMetadata(existingBooking);
    const payload = await createBookingPayload({
      slotStart,
      userName,
      userEmail,
      productId,
      userId,
      eventTypeSlug,
      teamSlug,
      eventTypeId,
      username,
      metadata,
      prsnlBookingId: bookingId,
    });

    // Call Cal.com API via Edge Function
    const data = await createBookingViaEdge(payload);
    logBookingCreated(data);

    // Confirm booking and sync staff
    const { shootingId, directorName, directorAvatar } = await confirmBookingAfterCalResponse(
      tempBookingUid, data.data?.uid, bookingId, userId, data
    );

    return {
      success: true,
      bookingId: data.data?.id,
      bookingUid: data.data?.uid,
      shootingId,
      directorName,
      directorAvatar
    };
  } catch (error) {
    return handleBookingError(error, 'réservation');
  }
}

/**
 * Builds legacy Calendly metadata for booking migration
 * DEPRECATED: Only needed for Calendly service transition to Cal.com
 */
function buildLegacyCalendlyMetadata(
  existingBooking?: { booking_calendly_uri?: string | null; booking_uid?: string | null; id?: string } | null
): Record<string, any> {
  const metadata: Record<string, any> = {};
  if (existingBooking?.booking_calendly_uri) {
    metadata.old_calendly_uri = existingBooking.booking_calendly_uri;
    if (existingBooking.booking_uid) metadata.old_booking_uid = existingBooking.booking_uid;
    if (existingBooking.id) metadata.old_booking_id = existingBooking.id;
  }
  return metadata;
}

/**
 * Logs booking creation success details
 */
function logBookingCreated(data: any): void {
  console.log('%c✅ CAL.COM - RÉSERVATION CRÉÉE', 'color: #10b981; font-weight: bold; font-size: 12px');
  console.log('Données complètes:', data);
  console.log('Hosts:', data.data?.hosts);
  console.log('EventType:', data.data?.eventType);
  console.log(`🎫 Booking ID: ${data.data?.id}`);
  console.log(`🔑 UID: ${data.data?.uid}`);
}

// Re-export utility functions for backward compatibility
export { formatSlotTime, formatSlotRange } from './calUtils';

/**
 * 3️⃣ Récupération des créneaux disponibles pour le RDV de préparation
 * Utilise le slug dynamique basé sur la durée du produit (45 ou 60 min)
 * 
 * @param date - Date pour laquelle récupérer les créneaux
 * @param userId - ID utilisateur pour récupérer la config Cal.com
 * @param productId - ID produit pour déterminer la durée
 * @returns Liste des créneaux disponibles ou erreur
 */
export async function getPreparationSlots(
  date: Date,
  userId: string,
  productId: string
): Promise<GetSlotsResponse> {
  const dateStr = date.toLocaleDateString('fr-FR');
  
  const product = await getProductById(productId);
  const preparationDurationMinutes = product?.preparation_duration_minutes || 45;
  
  const calConfig = await getCalSlugForBooking(productId, 'preparation', userId);
  
  const validationError = validateCalConfig(calConfig, 'preparation');
  if (validationError) return validationError;
  
  const { eventTypeSlug, teamSlug, username } = calConfig!;
  
  // Check if user is first-timer for editorial/preparation
  const isFirstTimer = await getUserEditorialFirstTimerStatus(userId);
  
  // For first-timers, use teamSlug instead of username
  const teamSlugToUse = isFirstTimer === true ? teamSlug : (username ? undefined : teamSlug);
  const usernameToUse = isFirstTimer === true ? undefined : username;

  try {
    const { start: startDate, end: endDate } = getDayDateRange(date);

    const data = await getMonthSlotsViaEdge(
      startDate.toISOString(),
      endDate.toISOString(),
      eventTypeSlug,
      teamSlugToUse,
      { username: usernameToUse }
    );
    
    const slotsData = parseCalApiResponse(data);
    const slots = transformSlotsToCalSlots(slotsData, preparationDurationMinutes);

    return {
      success: true,
      slots,
    };
  } catch (error) {
    return handleSlotsError(error, date, preparationDurationMinutes);
  }
}

/**
 * 4️⃣ Création d'une réservation pour le RDV de préparation
 * 
 * @param slotStart - Date/heure de début du créneau
 * @param userName - Nom de l'utilisateur
 * @param userEmail - Email de l'utilisateur
 * @param shootingDate - Date du tournage associé
 * @param userId - ID utilisateur (optionnel)
 * @param productId - ID produit (optionnel, pour logique personnalisée)
 */
export async function createPreparationBooking(
  slotStart: string,
  userName: string,
  userEmail: string,
  shootingDate: string,
  userId: string,
  productId: string,
  shootingId?: string | null
): Promise<CreateBookingResponse> {
  try {
    const calConfig = await getCalSlugForBooking(productId, 'preparation', userId);
    
    if (!calConfig) {
      return {
        success: false,
        error: 'Configuration Cal.com introuvable pour ce produit',
      };
    }
    
    const { eventTypeSlug, teamSlug, username, eventTypeId } = calConfig;
    
    const userResult = await getUserById(userId);
    const isFirstTimer = userResult.success && userResult.data ? !userResult.data.personal_brand_manager_id : true;
    
    const validationError = validateFirstTimerEventTypeId(isFirstTimer, eventTypeId, eventTypeSlug);
    if (validationError) {
      return validationError;
    }
    
    const product = await getProductById(productId);
    const durationMinutes = product?.preparation_duration_minutes || 45;
    const startTime = new Date(slotStart);
    const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
    const productName = product?.name || null;
    
    const tempBookingUid = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const pbmId = !isFirstTimer && userResult.data?.personal_brand_manager_id
      ? userResult.data.personal_brand_manager_id
      : undefined;
    const tempBookingResult = await createBookingInDb({
      user_id: userId,
      booking_uid: tempBookingUid,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      event_type_slug: eventTypeSlug,
      plan: productName,
      product_id: productId,
      status: 'pending',
      location_id: null,
      meeting_url: null,
      meeting_type: 'editorial' as any,
      shooting_id: shootingId || null,
      metadata: { productId, type: 'preparation' },
      ...(pbmId != null && pbmId !== '' && { personal_brand_manager_id: pbmId })
    });
    
    if (!tempBookingResult.success || !tempBookingResult.data) {
      return {
        success: false,
        error: 'Erreur lors de la création de la réservation',
      };
    }
    
    const bookingId = tempBookingResult.data.id;
    
    const payload = await createBookingPayload({
      slotStart,
      userName,
      userEmail,
      productId,
      userId,
      eventTypeSlug,
      teamSlug,
      eventTypeId,
      username,
      metadata: { shootingDate, type: 'preparation' },
      prsnlBookingId: bookingId,
    });

    // 🚀 Appel via l'Edge Function sécurisée
    const data = await createBookingViaEdge(payload);

    // Extraire le lien Google Meet de la réponse Cal.com
    const meetingUrl = data.data?.meetingUrl || data.data?.location?.link || null;

    await updateBookingByUid(tempBookingUid, {
      booking_uid: data.data?.uid,
      status: 'confirmed',
      meeting_url: meetingUrl
    });

    let pbmName: string | undefined;
    let pbmAvatar: string | undefined;
    
    if (userId && data.data) {
      const syncResult = await syncStaffFromCalBooking(
        data,
        bookingId,
        userId
      );
      
      if (syncResult.success) {
        pbmName = syncResult.pbmName;
        pbmAvatar = syncResult.pbmAvatar;
      }
    }

    return {
      success: true,
      bookingId: data.data?.id,
      bookingUid: data.data?.uid,
      pbmName,
      pbmAvatar
    };
  } catch (error) {
    return handleBookingError(error, 'RDV préparation');
  }
}

/**
 * 7️⃣ Obtenir les disponibilités du mois pour les RDV de préparation
 */
export async function getPreparationMonthAvailability(
  year: number,
  month: number,
  userId: string,
  productId: string
): Promise<Set<string>> {
  const calConfig = await getCalSlugForBooking(productId, 'preparation', userId);
  
  if (!calConfig) {
    return new Set<string>();
  }
  
  const { eventTypeSlug, teamSlug, username } = calConfig;
  const { start: startDate, end: endDate } = getMonthDateRange(year, month);
  
  // Check if user is first-timer for editorial/preparation
  const isFirstTimer = await getUserEditorialFirstTimerStatus(userId);
  
  // For first-timers, use teamSlug instead of username
  const teamSlugToUse = isFirstTimer === true ? teamSlug : (username ? undefined : teamSlug);
  const usernameToUse = isFirstTimer === true ? undefined : username;

  try {
    const data = await getMonthSlotsViaEdge(
      startDate.toISOString(),
      endDate.toISOString(),
      eventTypeSlug,
      teamSlugToUse,
      { username: usernameToUse }
    );
    const slotsData = parseCalApiResponse(data);
    const datesWithAvailability = extractDatesFromSlots(slotsData);

    return datesWithAvailability;
  } catch (error) {
    logger.error(`❌ Erreur lors du chargement des disponibilités de préparation pour ${year}-${String(month + 1).padStart(2, '0')}:`, error);
    return new Set<string>();
  }
}

/**
 * Annule une réservation Cal.com existante
 * 
 * COMPORTEMENT :
 * - Vérifie d'abord le statut dans Supabase pour éviter les doubles annulations
 * - Appelle l'API Cal.com via l'Edge Function sécurisée
 * - Met à jour le statut à 'cancelled' dans Supabase
 * - Gère le cas où la réservation est déjà annulée (synchronisation)
 * 
 * CAS PARTICULIERS :
 * - Si déjà annulé dans Supabase : retourne une erreur immédiatement
 * - Si déjà annulé dans Cal.com : synchronise Supabase et retourne une erreur
 * 
 * @param bookingUid - UID Cal.com de la réservation à annuler
 * @param cancellationReason - Raison de l'annulation (défaut: "Annulation par l'utilisateur")
 * @returns {Promise<CancelBookingResponse>}
 * 
 * @example
 * const result = await cancelBooking(
 *   '2aS5fYrUUmqhCk5DPB5HEL',
 *   'Changement de planning'
 * );
 * 
 * if (result.success) {
 *   console.log('Booking cancelled successfully');
 * } else {
 *   console.error('Cancellation failed:', result.error);
 * }
 */
export async function cancelBooking(
  bookingUid: string,
  cancellationReason: string = "Annulation par l'utilisateur"
): Promise<CancelBookingResponse> {
  try {
    const bookingResult = await getBookingByUid(bookingUid);
    
    if (bookingResult.success && bookingResult.data) {
      const booking = bookingResult.data;
      if (booking.status === 'cancelled') {
        return {
          success: false,
          error: 'Ce rendez-vous a déjà été annulé',
        };
      }
    }
    
    await cancelBookingViaEdge(bookingUid, cancellationReason);

    await updateBookingByUid(bookingUid, { status: 'cancelled' });

    return { success: true };
  } catch (error) {
    return await handleCancellationError(error, bookingUid, updateBookingByUid);
  }
}

/**
 * Cancels a booking by ID. When booking_uid is null or starts with "temp-" (never
 * confirmed on Cal.com), does not call Cal API; only sets status to cancelled and
 * calls Calendly webhook when booking_calendly_uri is set.
 */
export async function cancelBookingById(
  bookingId: string,
  cancellationReason: string = "Annulation par l'utilisateur"
): Promise<CancelBookingResponse> {
  const bookingResult = await getBookingById(bookingId);
  if (!bookingResult.success || !bookingResult.data) {
    return { success: false, error: 'Réservation introuvable' };
  }
  const booking = bookingResult.data as { status?: string; booking_uid?: string | null; booking_calendly_uri?: string | null };
  if (booking.status === 'cancelled') {
    return { success: false, error: 'Ce rendez-vous a déjà été annulé' };
  }
  const uid = booking.booking_uid;
  if (uid && !uid.startsWith('temp-')) {
    return cancelBooking(uid, cancellationReason);
  }
  const updateResult = await updateBooking(bookingId, { status: 'cancelled' });
  if (!updateResult.success) {
    return { success: false, error: updateResult.error instanceof Error ? updateResult.error.message : 'Erreur lors de la mise à jour' };
  }
  if (booking.booking_calendly_uri) {
    try {
      await callCalendlyCancelWebhookByBookingIdViaEdge(bookingId);
    } catch {
      // Webhook is best-effort; DB is already updated to cancelled, so consider success
    }
  }
  return { success: true };
}

/**
 * Reprogramme une réservation Cal.com existante
 * 
 * COMPORTEMENT :
 * - Récupère automatiquement la durée correcte du booking (shooting ou préparation)
 * - Appelle l'API Cal.com via l'Edge Function sécurisée
 * - Met à jour la nouvelle date/heure et le nouveau UID dans Supabase
 * - Invalide le cache de disponibilités pour la nouvelle date
 * - Gère le cas où la réservation est déjà annulée
 * 
 * DURÉE AUTOMATIQUE :
 * - Extrait le product_id du booking original
 * - Détermine si c'est un shooting ou une préparation
 * - Applique la bonne durée (shooting: 180-210min, préparation: 45-60min)
 * 
 * @param bookingUid - UID Cal.com de la réservation à reprogrammer
 * @param newStart - Nouvelle date/heure de début (format ISO 8601)
 * @param reschedulingReason - Raison de la reprogrammation (défaut: "Reprogrammation par l'utilisateur")
 * @returns {Promise<RescheduleBookingResponse>}
 * 
 * @example
 * const result = await rescheduleBooking(
 *   '2aS5fYrUUmqhCk5DPB5HEL',
 *   '2025-01-20T14:00:00Z',
 *   'Demande client'
 * );
 * 
 * if (result.success) {
 *   console.log('New booking UID:', result.bookingUid);
 * } else {
 *   console.error('Rescheduling failed:', result.error);
 * }
 */
export async function rescheduleBooking(
  bookingUid: string,
  newStart: string,
  reschedulingReason: string = "Reprogrammation par l'utilisateur"
): Promise<RescheduleBookingResponse> {
  try {
    const bookingResult = await getBookingByUid(bookingUid);
    if (!bookingResult.success || !bookingResult.data) {
      return {
        success: false,
        error: 'Réservation introuvable',
      };
    }
    
    const booking = bookingResult.data as any;
    
    // DEPRECATED only needed for Calendly service transition to Cal.com
    // If booking has booking_calendly_uri, create a new booking instead of rescheduling
    if ((booking as any).booking_calendly_uri) {
      // Get user info
      const userResult = await getUserById(booking.user_id);
      if (!userResult.success || !userResult.data) {
        return {
          success: false,
          error: 'Impossible de récupérer les informations utilisateur',
        };
      }
      
      const user = userResult.data;
      let userName = user.email?.split('@')[0] || 'User';
      const userEmail = user.email || '';
      
      // Get user profile for full name
      const { getUserProfile } = await import('../../data/users');
      const profileResult = await getUserProfile(booking.user_id);
      if (profileResult.success && profileResult.data) {
        const profile = profileResult.data;
        const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
        if (fullName) {
          userName = fullName;
        }
      }
      
      if (!booking.product_id) {
        return {
          success: false,
          error: 'Produit introuvable pour cette réservation',
        };
      }
      
      // Create new booking with same shooting_id, passing old booking info
      const createResult = await createBooking(
        newStart,
        userName,
        userEmail,
        booking.product_id,
        booking.user_id,
        booking.location_id || undefined,
        {
          booking_calendly_uri: (booking as any).booking_calendly_uri,
          shooting_id: (booking as any).shooting_id,
          booking_uid: (booking as any).booking_uid,
        }
      );
      
      if (!createResult.success) {
        return {
          success: false,
          error: createResult.error || 'Erreur lors de la création de la nouvelle réservation',
        };
      }
      
      return {
        success: true,
        bookingUid: createResult.bookingUid || '',
      };
    }
    
    // Normal reschedule flow for Cal.com bookings
    // 🚀 Appel via l'Edge Function sécurisée
    const data = await rescheduleBookingViaEdge(bookingUid, {
      start: newStart,
      reschedulingReason,
    });

    const newBookingUid = data.data?.uid;

    const startTime = new Date(newStart);
    const { durationMinutes } = await getBookingDuration(bookingUid);
    const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
    
    await updateBookingByUid(bookingUid, {
      booking_uid: newBookingUid,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
    });

    return {
      success: true,
      bookingUid: newBookingUid,
    };
  } catch (error) {
    // Cas spécial : booking déjà annulé
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('cancelled') || errorMessage.includes('not found')) {
      return {
        success: false,
        error: 'Ce rendez-vous ne peut pas être reprogrammé (déjà annulé ou introuvable)',
      };
    }
    
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Reschedules a booking by ID. When booking_uid is a real Cal.com UID, calls Cal.com
 * reschedule API. When booking_uid is null, "temp-", or Calendly-only: cancels the
 * current booking (DB only for temp-) and creates a new Cal.com booking at newStart.
 */
export async function rescheduleBookingById(
  bookingId: string,
  newStart: string,
  reschedulingReason: string = "Reprogrammation par l'utilisateur"
): Promise<RescheduleBookingResponse> {
  const bookingResult = await getBookingById(bookingId);
  if (!bookingResult.success || !bookingResult.data) {
    return { success: false, error: 'Réservation introuvable' };
  }
  type BookingRescheduleFields = {
    booking_uid?: string | null;
    booking_calendly_uri?: string | null;
    user_id: string;
    product_id?: string | null;
    location_id?: string | null;
    shooting_id?: string | null;
    meeting_type?: 'shooting' | 'editorial' | null;
  };
  const booking = bookingResult.data as unknown as BookingRescheduleFields;
  const uid = booking.booking_uid;
  if (uid && !uid.startsWith('temp-')) {
    return rescheduleBooking(uid, newStart, reschedulingReason);
  }
  if (!booking.product_id) {
    return { success: false, error: 'Produit introuvable pour cette réservation' };
  }
  const userResult = await getUserById(booking.user_id);
  if (!userResult.success || !userResult.data) {
    return { success: false, error: 'Impossible de récupérer les informations utilisateur' };
  }
  const user = userResult.data;
  let userName = user.email?.split('@')[0] || 'User';
  const userEmail = user.email || '';
  const { getUserProfile } = await import('../../data/users');
  const profileResult = await getUserProfile(booking.user_id);
  if (profileResult.success && profileResult.data) {
    const fullName = [profileResult.data.first_name, profileResult.data.last_name].filter(Boolean).join(' ');
    if (fullName) userName = fullName;
  }
  const isPreparation = booking.meeting_type === 'editorial';
  const createResult = isPreparation
    ? await createPreparationBooking(
        newStart,
        userName,
        userEmail,
        newStart.slice(0, 10),
        booking.user_id,
        booking.product_id,
        booking.shooting_id ?? undefined
      )
    : await createBooking(
        newStart,
        userName,
        userEmail,
        booking.product_id,
        booking.user_id,
        booking.location_id ?? undefined,
        {
          booking_calendly_uri: booking.booking_calendly_uri ?? undefined,
          shooting_id: booking.shooting_id ?? undefined,
          booking_uid: undefined,
          id: bookingId,
        }
      );
  if (!createResult.success) {
    return {
      success: false,
      error: createResult.error || 'Erreur lors de la création de la nouvelle réservation',
    };
  }
  await updateBooking(bookingId, { status: 'cancelled' });
  if (booking.booking_calendly_uri) {
    try {
      await callCalendlyCancelWebhookByBookingIdViaEdge(bookingId);
    } catch {
      // Best-effort; old booking is already marked cancelled and new one exists
    }
  }
  return {
    success: true,
    bookingUid: createResult.bookingUid || '',
  };
}
